import React, { useState, useEffect } from 'react';
import { getNotificationSettings, updateNotificationSettings } from '../api';
import { showError, fetchWithRetry } from '../utils';
import Loader from './layout/Loader';
import Page from './layout/Page';
import Card from './layout/Card';
import useAlert from '../hooks/useAlert';

export default function Notifiche() {
    const [settings, setSettings] = useState({
        notificapwatelricevuta: false,
        notificapwatelricevutaurg: false,
        notificapwaappuntamentonew: false,
        notificapwaappuntamentoedit: false,
        notificapwaappuntamentodelete: false,
        notificapwasogliaminuti: false
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const alert = useAlert();

    // Configurazione dei flag con le loro descrizioni
    const notificationConfig = [
        {
            key: 'notificapwatelricevuta',
            label: 'Telefonata ricevuta',
            description: 'Ricevi una notifica quando arriva una telefonata',
        },
        {
            key: 'notificapwatelricevutaurg',
            label: 'Telefonata ricevuta urgente',
            description: 'Notifica speciale per chiamate urgenti',
        },
        {
            key: 'notificapwaappuntamentonew',
            label: 'Appuntamento fissato',
            description: 'Quando la segretaria fissa un nuovo appuntamento',
        },
        {
            key: 'notificapwaappuntamentoedit',
            label: 'Appuntamento modificato',
            description: 'Quando la segretaria modifica un appuntamento esistente',
        },
        {
            key: 'notificapwaappuntamentodelete',
            label: 'Appuntamento annullato',
            description: 'Quando la segretaria annulla un appuntamento',
        },
        {
            key: 'notificapwasogliaminuti',
            label: 'Soglia minuti residui',
            description: 'Avviso quando i minuti a disposizione stanno per finire',
        }
    ];

    // Carica le impostazioni al mount
    useEffect(() => {
        let ignore = false;
        const loadSettings = async () => {
            setLoading(true);
            let data = await getNotificationSettings();
            data = Object.fromEntries(
                Object.entries(data).map(([k, v]) => [k, v ?? false])
            );
            if (ignore) return; 
            setSettings(data);
        };

        fetchWithRetry(loadSettings, ignore)
        .catch(err => {
            if (!ignore) {
                console.error('Errore nel caricamento impostazioni notifiche:', err);
                showError('Errore nel caricamento delle impostazioni: ' + err.message);
            }
        })
        .finally(() => {
            if (!ignore) {
                setLoading(false);
            }
        });

        return () => {
            ignore = true; // Imposta ignore a true quando il componente viene smontato
        }
    }, []);

    // Gestisce il cambio di stato di un flag
    const handleToggle = async (key) => {
        const newSettings = {
            ...settings,
            [key]: !settings[key]
        };
        
        setSettings(newSettings);
        
        // Salva immediatamente
        try {
            setSaving(true);
            await updateNotificationSettings(newSettings);
            alert({message: 'Impostazioni salvate con successo'});
        } catch (error) {
            console.error('Errore nel salvataggio:', error);
            // Ripristina il valore precedente in caso di errore
            setSettings(settings);
            showError('Errore nel salvataggio delle impostazioni: ' + error.message);
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return  <Page title={"Configurazione notifiche"} >
                <Loader />
            </Page>
    }

    return (
        <Page title={"Configurazione notifiche"} >
              <Card content={
                <div className="notifiche">
                    <div className="notifiche__header">
                        <p className="notifiche__subtitle">
                            Personalizza le notifiche che desideri ricevere
                        </p>
                    </div>

                    {/* Lista delle notifiche */}
                    <div className="notifiche__list">
                        {notificationConfig.map((config) => (
                            <div key={config.key} className="notifiche__item">
                                <div className="notifiche__item-toggle">
                                    <label className="notifiche__toggle">
                                        <input
                                            type="checkbox"
                                            checked={settings[config.key] || false}
                                            onChange={() => handleToggle(config.key)}
                                            disabled={saving}
                                            className="notifiche__toggle-input"
                                        />
                                        <span className="notifiche__toggle-slider"></span>
                                    </label>
                                </div>
                                <div className="notifiche__item-content">
                                    <div className="notifiche__item-info">
                                        <h3 className="notifiche__item-title">
                                            {config.label}
                                        </h3>
                                        <p className="notifiche__item-description">
                                            {config.description}
                                        </p>
                                    </div>
                                </div>                                
                            </div>
                        ))}

                        {/* Notifiche amministrative - sempre attive */}
                        <div className="notifiche__item notifiche__item--disabled">
                            <div className="notifiche__item-toggle">
                                <label className="notifiche__toggle notifiche__toggle--disabled">
                                    <input
                                        type="checkbox"
                                        checked={true}
                                        disabled={true}
                                        className="notifiche__toggle-input"
                                    />
                                    <span className="notifiche__toggle-slider"></span>
                                </label>
                            </div>
                            <div className="notifiche__item-content">
                                <div className="notifiche__item-info">
                                    <h3 className="notifiche__item-title">
                                        Notifiche amministrative
                                    </h3>
                                    <p className="notifiche__item-description">
                                        Comunicazioni importanti dal sistema (sempre attive)
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Informazioni aggiuntive */}
                    <div className="notifiche__info">
                        <h3 className="notifiche__info-title">Informazioni sulle notifiche</h3>
                        <ul className="notifiche__info-list">
                            <li>Le notifiche vengono inviate in tempo reale quando possibile</li>
                            <li>Assicurati di aver consentito le notifiche nel browser</li>
                            <li>Le notifiche amministrative non possono essere disattivate</li>
                            <li>Le modifiche vengono salvate automaticamente</li>
                        </ul>
                    </div>
                </div>
                } />
            </Page>
    );
}